import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getPreBookCartItems, PreBookHeader } from '../apis/retailer/preBookApis';

/**
 * Retailer pre-book cart.
 *
 * Each pre-book has its own cart on the server (lines are marked
 * type: "prebook" + preBookId and never appear in the normal cart). This slice
 * holds the cart of ONE pre-book at a time — the one the user is currently
 * looking at — and is intentionally NOT persisted, so a stale cart from
 * another pre-book can never leak in. The normal `cart` slice is untouched.
 */

// Same shape as a normal cart line + the pre-book `inPreBook` flag
export interface PreBookCartItem {
  Description: string;
  Item_Number: number;
  CaseCount: number;
  UOM: string;
  Price1: number;
  price: number;
  Tax_Rate: number;
  priceWithTax: number;
  isPriceChanged: boolean;
  oldPrice: number;
  newPrice: number;
  showDistributorImage: boolean;
  distributorImage: string | null;
  masterImage: string;
  UnitOunces: any;
  showLowStock: boolean;
  showTheInventoryStock: boolean;
  showWithOutPrice: boolean;
  allowToOrder: boolean;
  Inventory_OnHand: number;
  itemInActive?: boolean;
  // false when the admin removed the item from the pre-book after it was added to the cart
  inPreBook?: boolean;
  hasProductLimit?: boolean;
  productLimit?: number | null;
  hasMaxProductLimit?: boolean;
  maxProductLimit?: number | null;
  hasQtyDiscount?: boolean;
  qtyDiscount?: any;
  prepaidTaxRate?: number;
  Product: {
    id: number;
    Customer_Number: number;
    Item_Number: number;
    Price: number;
    Tax_Rate: number;
    Price_With_Tax: number;
    TotalPriceWithTax: number;
    Qty: number;
    TotalPrice: number;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    originalPrice: number;
    type?: string;
    preBookId?: number;
  };
}

interface PreBookCartState {
  preBookId: number | null;
  preBook: PreBookHeader | null;
  items: PreBookCartItem[];
  loading: boolean;
  error: string | null;
  count: number;
  totalItems: number;
  totalAmountWithTax: number;
  userItemLimitQty: number | null;
  userLimitMinOrderAmount: number | null;
  totalAmount: number;
}

const initialState: PreBookCartState = {
  preBookId: null,
  preBook: null,
  items: [],
  loading: false,
  error: null,
  count: 0,
  totalItems: 0,
  totalAmountWithTax: 0,
  userItemLimitQty: null,
  userLimitMinOrderAmount: null,
  totalAmount: 0,
};

// Fetch the cart of one pre-book (R5)
export const fetchPreBookCartItems = createAsyncThunk(
  'preBookCart/fetchPreBookCartItems',
  async (preBookId: number | string, { rejectWithValue }) => {
    try {
      const response: any = await getPreBookCartItems(preBookId);
      return { preBookId: Number(preBookId), data: response.data };
    } catch (error: any) {
      return rejectWithValue({ message: error?.response?.data?.message || error?.message || 'Failed to fetch pre-book cart items', status: error?.response?.status });
    }
  }
);

const preBookCartSlice = createSlice({
  name: 'preBookCart',
  initialState,
  reducers: {
    setPreBookCartCount: (state, action: PayloadAction<number>) => {
      state.count = action.payload;
    },
    // Local reset (after place order / clear cart / leaving the pre-book)
    clearPreBookCart: (state) => {
      state.items = [];
      state.count = 0;
      state.totalItems = 0;
      state.totalAmountWithTax = 0;
      state.totalAmount = 0;
    },
    resetPreBookCart: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPreBookCartItems.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        // Switching pre-book: drop the previous pre-book's lines immediately
        const nextId = Number(action.meta.arg);
        if (state.preBookId !== nextId) {
          state.preBookId = nextId;
          state.preBook = null;
          state.items = [];
          state.count = 0;
          state.totalItems = 0;
          state.totalAmountWithTax = 0;
          state.totalAmount = 0;
        }
      })
      .addCase(fetchPreBookCartItems.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload?.data;
        state.preBookId = action.payload?.preBookId ?? state.preBookId;
        state.preBook = payload?.preBook ?? state.preBook;
        state.items = payload?.finalCartItems || [];
        state.count = state.items.length;
        state.totalItems = payload?.totalItems || 0;
        state.totalAmountWithTax = payload?.totalAmountWithTax || 0;
        state.userItemLimitQty = payload?.userItemLimitQty || null;
        state.userLimitMinOrderAmount = payload?.userLimitMinOrderAmount || null;
        state.totalAmount = payload?.totalAmount || 0;
      })
      .addCase(fetchPreBookCartItems.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || action.error.message || 'Failed to fetch pre-book cart items';
      });
  },
});

export const { setPreBookCartCount, clearPreBookCart, resetPreBookCart } = preBookCartSlice.actions;

export default preBookCartSlice.reducer;
